<template>
  <RenderCacheable
    :cache-key="footerCacheKey"
    :no-cache="componentCachingDisabled"
  >
    <AppFooter
      :is-multi-language="isMultiLanguage"
      :top-level-domain="topLevelDomain"
      :language-and-country="languageAndCountry"
      :language-selector-props="languageSelectorProps"
      :footer-links="footerLinks"
      :trusted-payments-content="trustedPaymentsContent"
    />
  </RenderCacheable>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import type { Category } from '~~/types/commercetools';
import { getFooterLinks } from '~~/helpers/footer/getFooterLinks';
import { useCachingQueryParams } from '~/composables/useCachingQueryParams';
import { COMPONENTS } from '~~/constants/cms';

export default defineComponent({
  name: 'AppFooterWrapper',

  props: {
    categoriesContainer: {
      type: Array as PropType<Category[]>,
      default: () => {

        return [];

      },
    },
  },

  setup(props) {

    const { countryLocalization, languageAndCountry, countryCode } = useLocalization();
    const { isExtraGuaranteeEnabled, isLoyaltyDiscountEnabled } = useStoreVsfExtended();
    const { componentCachingDisabled } = useCachingQueryParams();
    const {
      loadComponent: loadTrustedPayments,
      internalCmsContent: trustedPaymentsContent,
    } = useInternalCms(COMPONENTS.TRUSTED_PAYMENTS.SLUG);

    useLazyAsyncData(`footer-trusted-payments-${languageAndCountry.value}`, async () => {

      await loadTrustedPayments();

      return trustedPaymentsContent.value;

    });

    const topLevelDomain = computed(() => countryLocalization.value.topLevelDomain);
    const isMultiLanguage = computed(() => countryLocalization.value.isMultiLanguage);

    const footerCacheKey = computed(() =>
      `footer-${languageAndCountry.value}-${isLoyaltyDiscountEnabled.value}-${isExtraGuaranteeEnabled.value}`,
    );

    const flagSvgs = import.meta.glob('~/assets/images/flags/*.svg', {
      eager: true,
      import: 'default',
    }) as Record<string, string>;

    const resolveFlagUrl = (flagFile: string | undefined | null): string | null => {

      if (!flagFile) {

        return null;

      }

      const normalizedFlagFile = flagFile.replaceAll('\\', '/');
      const matchedKey = Object.keys(flagSvgs).find((key) =>
        key.replaceAll('\\', '/').endsWith(`/assets/images/flags/${normalizedFlagFile}`),
      );

      return matchedKey ? flagSvgs[matchedKey] : null;

    };

    const fallbackFlagUrl = resolveFlagUrl('flag_en.svg') ?? '';

    const languageSelectorProps = computed(() => ({
      flagPath: resolveFlagUrl(countryLocalization.value?.flagFile) ?? fallbackFlagUrl,
      label: countryLocalization.value?.label ?? '',
      activeLanguageAndCountry: languageAndCountry.value,
      languageAndCountries: countryLocalization.value?.getLanguageAndCountries() ?? [],
    }));

    const footerLinks = computed(() =>
      getFooterLinks({
        categoriesContainer: props.categoriesContainer,
        countryCode: countryCode.value,
        isExtraGuaranteeEnabled: isExtraGuaranteeEnabled.value,
        isLoyaltyDiscountEnabled: isLoyaltyDiscountEnabled.value,
      }),
    );

    return {
      isMultiLanguage,
      languageAndCountry,
      topLevelDomain,
      isExtraGuaranteeEnabled,
      isLoyaltyDiscountEnabled,
      languageSelectorProps,
      footerLinks,
      footerCacheKey,
      componentCachingDisabled,
      trustedPaymentsContent,
    };

  },
});
</script>
